import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  model,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { CoinflipDeprecatedService } from '@features/games/coinflip/coinflip.service';
import { SessionService } from '@core/services/session.service';
import { Coinflip } from '@features/games/coinflip/interfaces/coinflip.interface';
import { CfMiddleComponent } from './cf-middle/cf-middle.component';
import { CfPlayerSideComponent } from './cf-player-side/cf-player-side.component';
import { BalanceIconComponent } from '@core/components/balance-icon/balance-icon.component';
import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';
import { sleep } from '@core/utils/sleep.util';
import { ClipboardService } from '@core/services/clipboard.service';
import { CoinflipSpritesService } from '@features/games/coinflip/coinflip-sprites.service';

const RUNNABLE_STATUSES: ReadonlyArray<Coinflip['fe_status']> = [
  'running',
  'finished',
];

@Component({
  selector: 'app-view-coinflip-modal',
  templateUrl: './view-coinflip-modal.component.html',
  styleUrls: ['./view-coinflip-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CfMiddleComponent,
    CfPlayerSideComponent,
    BalanceIconComponent,
    FairnessButtonComponent,
  ],
})
export class ViewCoinflipModalComponent implements OnInit {
  private deprecatedCoinflipService = inject(CoinflipDeprecatedService);
  public clipboardService = inject(ClipboardService);
  public session = inject(SessionService);
  private spritesService = inject(CoinflipSpritesService);

  coinflip = model.required<Coinflip>();
  showAnimation = signal(false);
  isStartedAnimation = signal(false);

  /**
   * Gameid we've already played the spin animation for in THIS modal
   * instance. Prevents the spin from replaying when downstream socket /
   * service updates re-emit the same finished game (the service runs
   * `sortGames()` ~10 s after the round finishes, which fires the games
   * signal twice and used to retrigger this animation).
   */
  private animatedGameId: number | null = null;

  constructor() {
    // Stay in sync with the service's games list while the modal is open.
    // Also picks up `fe_status` transitions (e.g. running → finished) so the
    // animation triggers when a live game ends with the modal already open.
    effect(() => {
      const games = this.deprecatedCoinflipService.games();
      untracked(() => {
        const gameid = this.coinflip().gameid;
        const updated = games.find((cf) => cf.gameid === gameid);
        if (updated) {
          this.coinflip.set(updated);
        }
        this.maybeStartAnimation();
      });
    });
  }

  ngOnInit(): void {
    // Inputs are guaranteed set by ngOnInit. Trigger animation immediately so
    // opening the modal on a finished coinflip from the lobby/history replays
    // the spin (matching how a live coinflip animates in real time).
    this.maybeStartAnimation();
  }

  private maybeStartAnimation(): void {
    if (this.isStartedAnimation()) return;
    const game = this.coinflip();
    if (this.animatedGameId === game.gameid) return;
    if (!RUNNABLE_STATUSES.includes(game.fe_status)) return;
    this.animatedGameId = game.gameid;
    void this.runAnimationCycle(game.fe_status === 'finished');
  }

  private async runAnimationCycle(introDelay: boolean): Promise<void> {
    this.isStartedAnimation.set(true);
    if (introDelay) {
      await sleep(500);
    }

    // Wait for the sprite atlas to be GPU-resident before playing audio or
    // flipping the canvas into its --playing state. Normally the atlases are
    // already loaded (the service kicks off their fetch + decode at app boot
    // and the user typically reaches a coinflip seconds later), in which
    // case this resolves on the next microtask. The guard matters on cold
    // starts — if the user lands on a coinflip immediately after page load
    // and the bitmap is still in flight, this prevents the audio from
    // playing while the canvas waits silently for its texture.
    const side = this.coinflip().winner?.side;
    if (side === 1 || side === 2) {
      try {
        await this.spritesService.getBitmap(side);
      } catch {
        // Loading failure is non-fatal — the spin will simply not animate;
        // the static <app-coinflip-coin> still renders the result.
      }
    }

    // Align the audio start with the canvas's first paint. The canvas
    // animation in cf-middle keys its time origin to the rAF tick after
    // `showAnimation.set(true)` — gating that signal flip behind a rAF here
    // pins the canvas's first draw to a stable paint frame.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve())
    );
    this.showAnimation.set(true);
    await sleep(this.deprecatedCoinflipService.RUNNING_DURATION);

    const latest = this.deprecatedCoinflipService
      .games()
      .find((cf) => cf.gameid === this.coinflip().gameid);
    this.coinflip.update((cf) => ({
      ...(latest ?? cf),
      fe_status: 'finished',
    }));
    this.showAnimation.set(false);
    this.isStartedAnimation.set(false);
  }
}
